import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components, MenuListProps } from "react-select";

type Option = { label: string; value: string };

type Props = {
  options: Array<string | number | null | undefined>;
  committed: string[];
  onCommit: (value: string[] | undefined) => void;
  placeholder?: string;
};

const _compactSelectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 34,
    height: 34,
    fontSize: 13,
    borderColor: "#d0d5dd",
    boxShadow: "none",
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: 34,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

export const DropdownForTanStackFilter: React.FC<Props> = ({
  options,
  committed,
  onCommit,
  placeholder = "All",
}) => {
  const selectRef = useRef<any>(null);

  // build options as strings
  const selectOptions = useMemo<Option[]>(() => {
    const set = new Set<string>();
    for (const v of options) {
      if (v === null || v === undefined || v === "") continue;
      set.add(String(v));
    }
    return Array.from(set)
      .sort()
      .map((v) => ({ label: v, value: v }));
  }, [options]);

  // menu open state (controlled so we can close after OK/Cancel)
  const [menuOpen, setMenuOpen] = useState(false);

  // temp state: user plays with this, not committed until OK
  const [temp, setTemp] = useState<string[]>(committed);

  // if committed changes externally (reset filters, etc.), sync temp
  useEffect(() => {
    setTemp(committed);
  }, [committed]);

  const tempAsOptions = useMemo(() => {
    const set = new Set(temp);
    return selectOptions.filter((o) => set.has(o.value));
  }, [temp, selectOptions]);

  const handleOk = () => {
    onCommit(temp.length ? temp : undefined);
    setMenuOpen(false);
    selectRef.current?.blur?.();
  };

  const handleCancel = () => {
    // "Cancel should set to All"
    setTemp([]);
    onCommit(undefined);
    setMenuOpen(false);
    selectRef.current?.blur?.();
  };


  const compactSelectStyles = {
    container: (base: any) => ({
      ...base,
      width: "100%",
      minWidth: 70,           // prevents collapse too far
    }),

    control: (base: any) => ({
      ...base,
      minHeight: 34,
      height: 34,
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
      borderColor: "#d0d5dd",
      boxShadow: "none",
    }),

    valueContainer: (base: any) => ({
      ...base,
      height: 34,
      padding: "0 8px",
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      overflow: "hidden",
      whiteSpace: "nowrap",
    }),

    placeholder: (base: any) => ({
      ...base,
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),

    singleValue: (base: any) => ({
      ...base,
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),

    indicatorsContainer: (base: any) => ({
      ...base,
      height: 34,
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
    }),

    clearIndicator: (base: any) => ({
      ...base,
      padding: 4,  // reduces width so it doesn't wrap
    }),

    dropdownIndicator: (base: any) => ({
      ...base,
      padding: 4,
    }),

    indicatorSeparator: (base: any) => ({
      ...base,
      marginTop: 6,
      marginBottom: 6,
    }),

    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({
      ...base,
      minWidth: 420,          // ✅ wider panel
      width: "max-content",   // ✅ grow to fit content if needed
      maxWidth: 700,          // ✅ safety
    }),

    menuList: (base: any) => ({
      ...base,
      maxHeight: 320,
    }),

    option: (base: any) => ({
      ...base,
      whiteSpace: "normal",   // ✅ allow wrapping
      isplay: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }),
  };


  // Custom MenuList with footer buttons
  const MenuList = (props: MenuListProps<Option, true>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div
          style={{
            position: "sticky",
            bottom: 0,
            background: "white",
            borderTop: "1px solid #e5e7eb",
            padding: 8,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
          // prevent click inside footer from closing menu due to blur
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={handleCancel}
            style={{
              height: 30,
              padding: "0 10px",
              borderRadius: 6,
              border: "1px solid #d0d5dd",
              background: "white",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleOk}
            style={{
              height: 30,
              padding: "0 10px",
              borderRadius: 6,
              border: "1px solid #2563eb",
              background: "#2563eb",
              color: "white",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            OK
          </button>
        </div>
      </components.MenuList>
    );
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        ref={selectRef}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        options={selectOptions}
        value={tempAsOptions}
        onChange={(newValue) => {
          const next = (newValue ?? []).map((x) => x.value);
          setTemp(next);
        }}
        menuIsOpen={menuOpen}
        onMenuOpen={() => setMenuOpen(true)}
        onMenuClose={() => setMenuOpen(false)}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
        styles={compactSelectStyles}
        maxMenuHeight={320}
        menuShouldScrollIntoView={false}
        isClearable={false}
        controlShouldRenderValue={false}
        placeholder={temp.length ? `${temp.length} selected` : placeholder}
        components={{ MenuList }}
        menuPlacement="auto"
      />
    </div>
  );
};


